import React, { ReactElement, useState } from "react";
import { DraggableData, Position, ResizableDelta, Rnd } from "react-rnd";

import { Size } from "../../../types/size";
import { classNames } from "../../../utils/classes";

import {
  CanvasElementMenu,
  CanvasElementMenuItemDelete,
  CanvasElementMenuItemDuplicate,
  CanvasElementMenuItemHeight,
  CanvasElementMenuItemMoveDown,
  CanvasElementMenuItemMoveUp,
  CanvasElementMenuWidth,
} from "./canvas-element-menu";
import { CanvasObject } from "./canvas-object";
import { DraggableHandle } from "./draggable-handle";
import { PackshotImage } from "./packshot-image";

export type CanvasElementProps = {
  canvasObject: CanvasObject;
  canvasObjectSize: Size;

  index: number;
  isGenerated: boolean;
  selected: boolean;
  canvasSize: Size;

  imageUrl?: string;
  imageComponent?: ReactElement;

  objectChanged: (data: Partial<Omit<CanvasObject, "kind">>) => void;
  objectDeleted: () => void;
  objectDuplicated: () => void;
  objectMovedUp: () => void;
  objectMovedDown: () => void;
  objectSelected: () => void;

  // Additional menu items
  menuItems?: ReactElement[];

  // Optional prop to ignore scaling when resizing the element
  ignoreScale?: boolean;

  // Locks the aspect ratio of the element when resizing
  lockAspectRatio?: boolean;

  // Callback for when the element is resized, providing the new size
  onResize?: (newSize: Size) => void;
};

export function CanvasElement(props: CanvasElementProps): ReactElement {
  const {
    canvasObject,
    canvasObjectSize,
    index,
    isGenerated,
    selected,
    canvasSize,
    objectChanged,
    objectDuplicated,
    objectDeleted,
    objectMovedUp,
    objectMovedDown,
    objectSelected,
    menuItems: additionalMenuItems,
    imageUrl,
    imageComponent,
    ignoreScale,
    lockAspectRatio,

    onResize,
  } = props;

  const canvasObjectScale = ignoreScale ? 1 : (canvasObject.scale ?? 1);
  const canvasObjectOffsetX = canvasObject.offset?.x ?? 0;
  const canvasObjectOffsetY = canvasObject.offset?.y ?? 0;
  const isPositionLocked = canvasObject.lockPosition === true;
  const isScaleLocked = canvasObject.lockScale === true;

  const [isDrag, setIsDrag] = useState<boolean>(false);

  const canvasObjectWidth = canvasObjectSize.width;
  const canvasObjectHeight = canvasObjectSize.height;

  function keepPositionWithinCanvasBounds(
    x: number,
    y: number,
    scale: number,
  ): { x: number; y: number } {
    if (x > canvasSize.width - 40) {
      x = canvasSize.width - 40;
    }

    if (x < -(canvasObjectWidth * scale - 40)) {
      x = -(canvasObjectWidth * scale - 40);
    }

    if (y > canvasSize.height - 40) {
      y = canvasSize.height - 40;
    }

    if (y < -(canvasObjectHeight * scale - 40)) {
      y = -(canvasObjectHeight * scale - 40);
    }

    return { x, y };
  }

  function handleDragStop(_e: any, d: DraggableData): void {
    if (!isPositionLocked) {
      const offset = keepPositionWithinCanvasBounds(
        d.x,
        d.y,
        canvasObjectScale,
      );
      objectChanged({
        offset,
      });
    }

    setIsDrag(false);
  }

  // Determine menu position based on element position and size, with adjustments to keep it within canvas bounds
  const menuItems = [
    <CanvasElementMenuItemDelete key={"delete"} onDelete={objectDeleted} />,
    <CanvasElementMenuItemMoveUp key={"moveUp"} onMoveUp={objectMovedUp} />,
    <CanvasElementMenuItemMoveDown
      key={"moveDown"}
      onMoveDown={objectMovedDown}
    />,
    ...(additionalMenuItems ?? []),
    <CanvasElementMenuItemDuplicate
      key={"duplicate"}
      onDuplicate={objectDuplicated}
    />,
  ];
  const menuHeight = menuItems.length * CanvasElementMenuItemHeight;
  const menuPosition = {
    x: canvasObjectOffsetX + canvasObjectWidth * canvasObjectScale,
    y:
      canvasObjectOffsetY +
      (canvasObjectHeight * canvasObjectScale - menuHeight) / 2,
  };

  // Keep menu position within canvas bounds
  if (menuPosition.x > canvasSize.width - CanvasElementMenuWidth) {
    // Try left side of the element first
    menuPosition.x = canvasObjectOffsetX - CanvasElementMenuWidth;

    // If it still overflows, position it at the right edge of the canvas
    if (menuPosition.x < 0) {
      menuPosition.x = canvasSize.width - CanvasElementMenuWidth;
    }
  }

  if (menuPosition.y > canvasSize.height - menuHeight) {
    // Try above the element first
    menuPosition.y = canvasObjectOffsetY;

    // If it still overflows, position it at the bottom edge of the canvas
    if (menuPosition.y < 0) {
      menuPosition.y = canvasSize.height - menuHeight;
    }
  }

  function handleResizeStop(
    _e: MouseEvent | TouchEvent,
    _dir: unknown,
    elementRef: HTMLElement,
    _delta: ResizableDelta,
    position: Position,
  ): void {
    let newObjectScale = undefined;
    if (!ignoreScale && !isScaleLocked) {
      newObjectScale = (elementRef.clientWidth * 100) / canvasObjectWidth / 100;
    }

    let newObjectOffset = undefined;
    if (!isPositionLocked) {
      newObjectOffset = keepPositionWithinCanvasBounds(
        position.x,
        position.y,
        newObjectScale ?? canvasObjectScale,
      );
    }

    objectChanged({
      scale: newObjectScale,
      offset: newObjectOffset,
    });

    onResize?.({
      width: elementRef.clientWidth,
      height: elementRef.clientHeight,
    });

    setIsDrag(false);
  }

  return (
    <>
      <Rnd
        className={classNames(
          "border",
          selected ? "border-white" : "border-transparent",
        )}
        style={{ zIndex: index }}
        size={{
          width: canvasObjectWidth * canvasObjectScale,
          height: canvasObjectHeight * canvasObjectScale,
        }}
        disableDragging={!selected || isGenerated || isPositionLocked}
        position={canvasObject.offset ?? { x: 0, y: 0 }}
        enableResizing={selected && !isGenerated && !isScaleLocked}
        lockAspectRatio={lockAspectRatio}
        onMouseDown={() => (isGenerated ? undefined : objectSelected())}
        onDragStop={(e, d) => handleDragStop(e, d)}
        onDrag={() => {
          setIsDrag(true);
        }}
        onResize={() => setIsDrag(true)}
        onResizeStop={handleResizeStop}
        resizeHandleComponent={
          selected
            ? {
                topLeft: <DraggableHandle />,
                topRight: <DraggableHandle />,
                bottomLeft: <DraggableHandle />,
                bottomRight: <DraggableHandle />,
              }
            : undefined
        }
      >
        {imageComponent
          ? imageComponent
          : imageUrl && <PackshotImage imageSrc={imageUrl} />}
      </Rnd>
      {!isDrag && selected && !isGenerated && (
        <CanvasElementMenu position={menuPosition}>
          {menuItems}
        </CanvasElementMenu>
      )}
    </>
  );
}
